import { useEffect, RefObject } from 'react';
import mixpanel from 'mixpanel-browser';
import { MIXPANEL_API_KEY } from './constants/mixpanel';
import { LineItem, User } from './types';

export interface TrackProps {
  event: string;
  data: {
    [prop: string]: LineItem | string | number | boolean | undefined;
  };
}

let initialized = false;

export const init = () =>
  new Promise((resolve, reject) => {
    if (MIXPANEL_API_KEY) {
      if (!initialized) {
        mixpanel.init(MIXPANEL_API_KEY);
        initialized = true;
        // @ts-ignore
        window.mixpanel = mixpanel;
      }

      return resolve();
    }

    return reject();
  });

export const identify = (id: string) =>
  init()
    .then(() => {
      mixpanel.identify(id);

      return Promise.resolve();
    })
    .catch(() => Promise.resolve());

export const alias = (id: string) =>
  init()
    .then(() => {
      mixpanel.alias(id);

      return Promise.resolve();
    })
    .catch(() => Promise.resolve());

// @ts-ignore
export const track = (props: TrackProps, cb?: RequestOptions) =>
  init()
    .then(() => {
      // @ts-ignore
      mixpanel.track(props.event, props.data, cb);

      return Promise.resolve();
    })
    .catch(() => Promise.resolve());

export const setUserInfo = (user: User) =>
  init()
    .then(() => {
      const {
        created_at: createdAt,
        consumer_attributes: { age, gender },
        id,
        ip_address: ipAddress,
      } = user;

      const userInfo = {
        $created: createdAt,
        Age: age,
        'EverlyWell user ID': id,
        Gender: gender,
        'IP address': ipAddress,
      };

      mixpanel.people.set(userInfo);
      mixpanel.identify(id.toString());

      return Promise.resolve();
    })
    .catch(() => Promise.resolve());

/*
 * handle events for analytics tracking
 *
 * required attribute on the HTML element:
 * * data-analytics-event
 *
 * optional attributes:
 * * data-analytics-label
 * * data-analytics-category
 * * data-analytics-test-id
 *
 */
// export function eventHandler(event: Event) {
export const eventHandler = (event: Event) => {
  event.preventDefault();
  const element = event.target as HTMLAnchorElement;
  const { href } = element;
  // tracking requires data attributes
  if (!element || !element.dataset) return;

  const {
    analyticsEvent,
    analyticsLabel,
    analyticsCategory,
    analyticsTestId,
  } = element.dataset;

  // event is required
  if (!analyticsEvent) return;
  track(
    {
      data: {
        category: analyticsCategory,
        label: analyticsLabel,
        test_id: analyticsTestId,
      },
      event: analyticsEvent,
    },
    () => {
      if (href) {
        window.location.href = href;
      }
    },
  );
};

const handleTrackableLinkClick = (e: Event) => {
  eventHandler(e);
};

const useAttachTrackingToLinks = (ref: RefObject<HTMLElement>) => {
  useEffect(() => {
    let trackableLinks: NodeListOf<HTMLAnchorElement> | null;
    if (ref.current) {
      trackableLinks = ref.current.querySelectorAll('[data-analytics-event]');
      if (trackableLinks) {
        trackableLinks.forEach(link =>
          link.addEventListener('click', handleTrackableLinkClick),
        );
      }
    }

    return () => {
      if (trackableLinks) {
        trackableLinks.forEach(link =>
          link.removeEventListener('click', handleTrackableLinkClick),
        );
      }
    };
  }, [ref.current]);
};

export default {
  identify,
  alias,
  setUserInfo,
  track,
  eventHandler,
  useAttachTrackingToLinks,
};
